import React from 'react'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleExclamation } from '@fortawesome/pro-regular-svg-icons'
import { CheckBoxFieldv2 } from '@labrav/react-components'
import {
  FundingCalculatorResource,
  PocCalculatorEdit,
} from '../../api/fundingApi'
import { useStyles } from './CalculatorResourceRows.styles'

const DYNAMICALLY_SELECTED_TOOLTIP =
  'When enabled, the AI agent picks the value for this field each time it drafts a request, using context like the customer, the opportunity details and past submissions. Any default value is ignored. When disabled, the field uses the fixed value you set here. You can always review and edit AI-selected values before submitting.'

interface CalculatorResourceRowsProps {
  resources: FundingCalculatorResource[]
  onEdit?: (edit: PocCalculatorEdit) => void
  scaling?: {
    scaledResourceIds: string[]
    onToggle: (resourceId: string) => void
  }
}

export const CalculatorResourceRows: React.FC<CalculatorResourceRowsProps> = ({
  resources,
  onEdit,
  scaling,
}) => {
  const { classes, cx } = useStyles()
  return (
    <div
      className={cx(classes.table, {
        [classes.tableWithScaling]: Boolean(scaling),
      })}
    >
      <div className={classes.headerRow}>
        {scaling ? (
          <Typography component="span" className={classes.label}>
            Dynamically selected
            <Tooltip title={DYNAMICALLY_SELECTED_TOOLTIP} arrow placement="top">
              <span className={classes.infoIcon}>
                <FontAwesomeIcon icon={faCircleExclamation} />
              </span>
            </Tooltip>
          </Typography>
        ) : null}
        {['Service', 'Quantity'].map(header => (
          <Typography key={header} component="span" className={classes.label}>
            {header}
          </Typography>
        ))}
        {['Upfront cost', 'Monthly cost'].map(header => (
          <Typography
            key={header}
            component="span"
            className={cx(classes.label, classes.amount)}
          >
            {header}
          </Typography>
        ))}
        <Tooltip
          title="Total 12 months cost (including upfront cost)"
          arrow
          placement="top"
        >
          <Typography
            component="span"
            className={cx(classes.label, classes.amount, classes.truncated)}
          >
            Total 12 months
          </Typography>
        </Tooltip>
      </div>
      {resources.map(resource => {
        const quantities = resource.fields.filter(
          field => field.scalable && field.current_value
        )
        const scaled = scaling?.scaledResourceIds.includes(resource.resource_id)
        const commitEdit = (change: {
          values?: Record<string, string>
          monthly_usd?: string
        }) =>
          onEdit?.({
            resource_id: resource.resource_id,
            values: {
              ...Object.fromEntries(
                quantities.map(field => [
                  field.field_id,
                  field.current_value ?? '',
                ])
              ),
              ...change.values,
            },
            monthly_usd:
              change.monthly_usd ?? String(resource.monthly_cost_usd),
          })
        return (
          <div
            key={resource.resource_id}
            className={classes.row}
            data-testid={`calculator-resource-${resource.resource_id}`}
          >
            {scaling ? (
              <Tooltip
                title={DYNAMICALLY_SELECTED_TOOLTIP}
                arrow
                placement="top"
              >
                <div className={classes.checkCell}>
                  <CheckBoxFieldv2
                    keyName={`scalable-${resource.resource_id}`}
                    title=""
                    value={Boolean(scaled)}
                    disabled={!resource.fields.some(field => field.scalable)}
                    onChangeBox={() => scaling.onToggle(resource.resource_id)}
                  />
                </div>
              </Tooltip>
            ) : null}
            <Typography component="span" className={classes.value}>
              {resource.service_name}
            </Typography>
            <div className={classes.quantityCell}>
              {quantities.length === 0 ? (
                <Typography component="span" className={classes.value}>
                  -
                </Typography>
              ) : null}
              {quantities.map(field =>
                onEdit ? (
                  <label key={field.field_id} className={classes.quantityField}>
                    <Typography component="span" className={classes.label}>
                      {`${field.label}${field.unit ? ` (${field.unit})` : ''}`}
                    </Typography>
                    <input
                      key={field.current_value}
                      className={classes.numberInput}
                      type="number"
                      min={0}
                      step="any"
                      defaultValue={field.current_value ?? ''}
                      onKeyDown={event => {
                        if (event.key === 'Enter') event.currentTarget.blur()
                      }}
                      onBlur={event => {
                        const value = event.currentTarget.value.trim()
                        if (Number(value) < 0) {
                          event.currentTarget.value = field.current_value ?? ''
                          return
                        }
                        if (!value || value === field.current_value) return
                        commitEdit({ values: { [field.field_id]: value } })
                      }}
                    />
                  </label>
                ) : (
                  <Typography
                    key={field.field_id}
                    component="span"
                    className={cx(classes.value, { [classes.struck]: scaled })}
                  >
                    {`${field.label}: ${field.current_value}${field.unit ? ` ${field.unit}` : ''}`}
                  </Typography>
                )
              )}
            </div>
            {(
              [
                ['upfront', resource.upfront_cost_usd],
                ['monthly', resource.monthly_cost_usd],
                [
                  'total',
                  resource.upfront_cost_usd + 12 * resource.monthly_cost_usd,
                ],
              ] as const
            ).map(([column, amount]) =>
              onEdit && column === 'monthly' ? (
                <input
                  key={`${column}-${amount}`}
                  className={cx(classes.numberInput, classes.amountInput)}
                  type="number"
                  min={0}
                  step="any"
                  aria-label="Monthly cost"
                  defaultValue={String(amount)}
                  onKeyDown={event => {
                    if (event.key === 'Enter') event.currentTarget.blur()
                  }}
                  onBlur={event => {
                    const value = event.currentTarget.value.trim()
                    if (Number(value) < 0) {
                      event.currentTarget.value = String(amount)
                      return
                    }
                    if (!value || Number(value) === amount) return
                    commitEdit({ monthly_usd: value })
                  }}
                />
              ) : (
                <Typography
                  key={column}
                  component="span"
                  className={cx(classes.value, classes.amount, {
                    [classes.struck]: scaled,
                  })}
                >
                  <Typography component="span" className={classes.currency}>
                    $
                  </Typography>
                  {amount.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
              )
            )}
          </div>
        )
      })}
    </div>
  )
}
